import './style.css';
import type { ItemEvent, MenuResult, RaceConfig, RaceResult } from './core/types';
import { CHARACTERS } from './data/roster';
import { TRACKS } from './data/tracks';
import { Track } from './game/track';
import { World } from './game/world';
import { GameRenderer } from './render/renderer';
import { buildMinimap, buildTrackLayer, type TrackLayer } from './render/trackRenderer';
import { InputManager } from './core/input';
import { devices } from './core/control-config';
import { createLoop, type Loop } from './core/loop';
import { AudioEngine } from './core/audio';
import { createMenu, showResults } from './ui/menu';
import { showSplash } from './ui/splash';
import { clearQuip, showQuip } from './ui/quips';
import { createTouchControls, type TouchControls } from './ui/touch';
import { isMobileDevice, isTouchDevice } from './core/device';
import { quipFor, type QuipKind } from './data/quips';
import { showBattleResult, showPause, showStartHint, type ControlPlayer, type Overlay } from './ui/controls';

const canvasEl = document.querySelector<HTMLCanvasElement>('#game');
const uiEl = document.querySelector<HTMLElement>('#ui');
if (!canvasEl || !uiEl) throw new Error('Hiányzó #game vagy #ui elem.');
const canvas: HTMLCanvasElement = canvasEl;
const uiRoot: HTMLElement = uiEl;

const input = new InputManager(window);
const audio = new AudioEngine();

if (import.meta.env.DEV) {
  (window as unknown as { __audio?: AudioEngine }).__audio = audio;
}

interface BuiltTrack {
  track: Track;
  layer: TrackLayer;
  minimap: HTMLCanvasElement;
}

const trackCache = new Map<string, BuiltTrack>();

const getTrack = (id: string): BuiltTrack => {
  const cached = trackCache.get(id);
  if (cached) return cached;
  const def = TRACKS.find((t) => t.id === id) ?? TRACKS[0];
  const t0 = performance.now();
  const track = new Track(def);
  const t1 = performance.now();
  const layer = buildTrackLayer(track);
  const t2 = performance.now();
  const minimap = buildMinimap(track, 200);
  const t3 = performance.now();
  if (import.meta.env.DEV) {
    console.info(
      `[track] ${def.id} · geometria ${(t1 - t0).toFixed(0)} ms · réteg ${(t2 - t1).toFixed(0)} ms · minimap ${(t3 - t2).toFixed(0)} ms`,
    );
  }
  const built = { track, layer, minimap };
  trackCache.set(id, built);
  return built;
};

const announce = (text: string): HTMLElement => {
  const div = document.createElement('div');
  div.className = 'loading';
  div.textContent = text;
  uiRoot.appendChild(div);
  return div;
};

const startRace = (choice: MenuResult): { world: World; renderer: GameRenderer } => {
  const def = TRACKS.find((t) => t.id === choice.trackId) ?? TRACKS[0];
  const { track, layer, minimap } = getTrack(def.id);
  const params = new URLSearchParams(window.location.search);
  const lapsParam = Number(params.get('laps'));
  const config: RaceConfig = {
    mode: choice.mode,
    laps: Number.isFinite(lapsParam) && lapsParam > 0 ? Math.min(9, Math.round(lapsParam)) : track.def.laps,
    trackId: track.def.id,
    picks: choice.picks,
    seed: (Date.now() ^ Math.floor(Math.random() * 0xffffffff)) >>> 0,
    difficulty: 0.62,
    autopilot: params.get('autopilot') === '1',
    practice: choice.practice === true || params.get('practice') === '1',
    battle: choice.battle === true || params.get('battle') === '1',
    night: choice.night === true || params.get('night') === '1',
    noAi: choice.noAi === true || params.get('noai') === '1',
  };
  const world = new World({ track, config });
  audio.setMusic('race');
  const renderer = new GameRenderer(canvas, world, layer, minimap);
  if (import.meta.env.DEV) {
    (window as unknown as {
      __csf?: { world: World; renderer: GameRenderer; input: InputManager; devices: typeof devices; audio: AudioEngine };
    }).__csf = { world, renderer, input, devices, audio };
  }
  return { world, renderer };
};

type RaceOutcome = 'finished' | 'restart' | 'menu';

const race = (world: World, renderer: GameRenderer): Promise<RaceOutcome> =>
  new Promise((resolve) => {
    let lastCountdown: number | null = null;
    let finishing = false;
    let paused = false;
    let done = false;
    let pauseOverlay: Overlay | null = null;
    let touch: TouchControls | null = null;
    let hint: Overlay | null = null;
    let loop: Loop;
    let finishTimer = 0;
    let quipAt = -99;
    let startQuipped = false;
    let lastLapQuipped = false;
    let finishQuipped = false;

    const maybeQuip = (kart: World['players'][number], kind: QuipKind): void => {
      if (done || paused) return;
      if (kind !== 'finish' && world.time - quipAt < 6) return;
      quipAt = world.time;
      showQuip(uiRoot, kart.character, quipFor(kart.character.id, kind));
    };

    const controlPlayers = (): ControlPlayer[] =>
      world.players.map((k) => ({
        playerIndex: k.playerIndex,
        characterName: k.character.short,
      }));

    const cleanup = (): void => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('keydown', onRaceKey, true);
      window.clearTimeout(finishTimer);
      // a motorhang ne ragadjon bent a menüben / eredményképernyőn
      audio.stopEngines();
      audio.setMusic('menu');
      clearQuip();
      input.setTouchState(null);
      touch?.destroy();
      touch = null;
      hint?.close();
      hint = null;
      pauseOverlay?.close();
      pauseOverlay = null;
    };

    const finish = (outcome: RaceOutcome): void => {
      if (done) return;
      done = true;
      loop.stop();
      cleanup();
      resolve(outcome);
    };

    const openPause = (): void => {
      if (paused || done || world.phase === 'finished') return;
      paused = true;
      audio.stopEngines();
      touch?.hide();
      hint?.close();
      hint = null;
      pauseOverlay = showPause(uiRoot, {
        players: controlPlayers(),
        musicOn: audio.musicEnabled,
        onToggleMusic: () => audio.toggleMusic(),
        sfxOn: audio.sfxEnabled,
        onToggleSfx: () => audio.toggleSfx(),
        onResume: () => {
          paused = false;
          pauseOverlay = null;
          touch?.show();
        },
        onRestart: () => finish('restart'),
        onMenu: () => finish('menu'),
      });
    };

    const onRaceKey = (e: KeyboardEvent): void => {
      if (e.code !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      if (paused) {
        pauseOverlay?.close();
        pauseOverlay = null;
        paused = false;
      } else {
        openPause();
      }
    };

    loop = createLoop({
      update(dt) {
        input.poll();
        if (paused) return;

        for (let i = 0; i < world.players.length; i++) world.setPlayerInput(i, input.state(i));
        world.step(dt);

        const events = world.drainEvents();
        for (const e of events) {
          handleItemEvent(e, renderer);
          if (e.kind === 'hit' && e.targetId !== undefined) {
            const target = world.karts.find((k) => k.id === e.targetId);
            if (target?.isPlayer) maybeQuip(target, 'hit');
          }
        }
        for (const b of world.drainBumps()) {
          audio.sfx('bump');
          renderer.fx.burst(b.at.x, b.at.y, '#ffe066', Math.round(3 + b.strength * 6), 80);
        }
        renderer.fx.update(dt, world.karts);
        if (touch) touch.setSpeed(world.players[0]?.speedKmh ?? 0);

        if (!startQuipped && world.phase === 'racing' && world.time > 0.4) {
          startQuipped = true;
          const first = world.players[0];
          if (first) maybeQuip(first, 'start');
        }
        if (!lastLapQuipped && world.phase === 'racing') {
          const lastLap = world.players.find((k) => k.lap === world.config.laps && k.progress > 0.04);
          if (lastLap) {
            lastLapQuipped = true;
            maybeQuip(lastLap, 'lastlap');
          }
        }

        const cd = world.hudCountdown();
        if (cd !== null && cd !== lastCountdown) {
          lastCountdown = cd;
          audio.countdown(cd);
        }

        audio.updateEngines(
          world.players.map((k) => ({
            speed: Math.hypot(k.vel.x, k.vel.y),
            maxSpeed: k.stats.topSpeed,
            throttle: k.input.throttle,
            active: !k.finished,
          })),
        );

        if (world.phase === 'finished' && !finishing) {
          finishing = true;
          if (!finishQuipped) {
            finishQuipped = true;
            const first = world.players[0];
            if (first) maybeQuip(first, 'finish');
          }
          audio.sfx('finish');
          audio.stopEngines();
          finishTimer = window.setTimeout(() => finish('finished'), 1100);
        }
      },
      render() {
        renderer.render();
      },
    });

    const onResize = (): void => renderer.resize();
    window.addEventListener('resize', onResize);
    window.addEventListener('keydown', onRaceKey, true);

    if (!world.config.autopilot) hint = showStartHint(uiRoot, controlPlayers(), isTouchDevice() ? 5000 : 7000, isTouchDevice());
    if (isTouchDevice()) {
      touch = createTouchControls(uiRoot, { onPause: () => openPause() });
      input.setTouchState(touch.state);
      if (import.meta.env.DEV) {
        (window as unknown as { __touch?: TouchControls }).__touch = touch;
      }
    }
    loop.start();
  });

const handleItemEvent = (e: ItemEvent, renderer: GameRenderer): void => {
  switch (e.kind) {
    case 'pickup':
      audio.sfx('pickup');
      renderer.fx.burst(e.at.x, e.at.y, '#ffc857', 10, 110);
      break;
    case 'use':
      audio.sfx('use');
      break;
    case 'hit':
      audio.sfx('hit');
      renderer.fx.burst(e.at.x, e.at.y, '#ff5c4d', 18, 190);
      break;
    case 'blocked':
      audio.sfx('block');
      renderer.fx.burst(e.at.x, e.at.y, '#3ec6c0', 12, 150);
      break;
    case 'explode':
      audio.sfx('hit');
      renderer.fx.burst(e.at.x, e.at.y, '#8fd14f', 12, 140);
      break;
    default:
      break;
  }
};

const boot = async (): Promise<void> => {
  const unlock = (): void => {
    void audio.start();
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
  };
  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);
  window.addEventListener('keydown', (e) => {
    if (e.code === 'KeyM') audio.toggleMute();
  });

  let choice: MenuResult | null = null;
  let splashDone = false;
  const params = new URLSearchParams(window.location.search);
  const autoMode = Number(params.get('mode') ?? 0);
  if (autoMode === 1 || autoMode === 2 || autoMode === 4) {
    const fallback = CHARACTERS.map((c) => c.id);
    const picks = (params.get('picks') ?? fallback.join(','))
      .split(',')
      .map((s) => s.trim())
      .filter((id) => CHARACTERS.some((c) => c.id === id))
      .slice(0, autoMode);
    while (picks.length < autoMode) picks.push(fallback[picks.length]);
    choice = {
      mode: autoMode as 1 | 2 | 4,
      picks,
      trackId: params.get('track') ?? TRACKS[0].id,
    };
  }

  for (;;) {
    if (!choice && !splashDone) {
      splashDone = true;
      await showSplash(uiRoot);
      void audio.start();
      audio.setMusic('menu');
    }
    if (!choice) {
      const menu = createMenu(uiRoot, {
        characters: CHARACTERS,
        tracks: TRACKS,
        mobile: isMobileDevice(),
        sound: {
          get musicEnabled() {
            return audio.musicEnabled;
          },
          get sfxEnabled() {
            return audio.sfxEnabled;
          },
          get muted() {
            return audio.muted;
          },
          toggleMusic: () => audio.toggleMusic(),
          toggleSfx: () => audio.toggleSfx(),
          toggleMute: () => {
            audio.toggleMute();
            return audio.muted;
          },
        },
      });
      choice = await menu.show();
      menu.hide();
      void audio.start();
    }

    const loading = announce('PÁLYA ÉPÍTÉSE…');
    await new Promise((r) => setTimeout(r, 30));
    const current: MenuResult = choice;
    const { world, renderer } = startRace(current);
    loading.remove();

    const outcome = await race(world, renderer);

    if (outcome === 'menu') {
      choice = null;
      continue;
    }
    if (outcome === 'restart') {
      continue;
    }

    const result: RaceResult = world.result ?? { trackName: world.track.def.name, mode: current.mode, entries: [] };
    const after: 'restart' | 'menu' = await new Promise((resolve) => {
      if (world.config.battle && result.battle) {
        const overlay = showBattleResult(uiRoot, result, {
          onRestart: () => {
            overlay.close();
            resolve('restart');
          },
          onMenu: () => {
            overlay.close();
            resolve('menu');
          },
        });
        return;
      }
      const cleanup = showResults(uiRoot, result, {
        onRestart: () => {
          cleanup();
          resolve('restart');
        },
        onMenu: () => {
          cleanup();
          resolve('menu');
        },
      });
    });

    if (after === 'menu') choice = null;
  }
};

void boot();
